import React from 'react';
import { useNavigate } from 'react-router-dom';

const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      title: "Connect Your Wallet",
      description: "Connect your Solana wallet to get started. We support Phantom wallet.",
      icon: "🔗"
    },
    {
      number: 2,
      title: "Create Donation Page",
      description: "Set up your donation page with a custom description, minimum donation amount, and your Solana wallet address.",
      icon: "📝"
    },
    {
      number: 3,
      title: "Get Your URLs",
      description: "Receive two important URLs: one for OBS integration and another for your stream description.",
      icon: "🔗"
    },
    {
      number: 4,
      title: "Setup OBS Source",
      description: "Add the OBS URL as a Browser Source in OBS Studio. Use chroma key effect and position it perfectly on your stream.",
      icon: "📺"
    },
    {
      number: 5,
      title: "Share Your Stream",
      description: "Add the donation URL to your stream description. Now you're ready to receive real-time donations!",
      icon: "🚀"
    }
  ];

  const handleGetStarted = () => {
    navigate('/create');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="how-it-works-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              How It <span className="streamlab-text">Works</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Get started with StreamLab in just 5 simple steps. From wallet connection to live donations on your stream.
            </p>
            <button
              onClick={handleGetStarted}
              className="btn-primary btn-lg"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="steps-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={step.number} className="step-card">
                <div className="step-number">
                  <span className="step-icon">{step.icon}</span>
                  <div className="step-badge">{step.number}</div>
                </div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Instructions */}
      <section className="detailed-instructions">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Detailed Setup Guide
          </h2>
          
          <div className="space-y-8">
            {/* Step 1 Details */}
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-number">1</span>
                <h3 className="instruction-title">Connect Your Wallet</h3>
              </div>
              <div className="instruction-content">
                <p>Click the "Connect Wallet" button in the top right corner and select your preferred Solana wallet.</p>
                <div className="instruction-tips">
                  <h4>Supported Wallets:</h4>
                  <ul>
                    <li>• Phantom</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 2 Details */}
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-number">2</span>
                <h3 className="instruction-title">Create Your Donation Page</h3>
              </div>
              <div className="instruction-content">
                <p>Fill out the donation page form with your details:</p>
                <ul className="instruction-list">
                  <li>• <strong>Description:</strong> What your donations will support</li>
                  <li>• <strong>Minimum Amount:</strong> Set your minimum donation (in SOL)</li>
                  <li>• <strong>Wallet Address:</strong> Your Solana wallet for receiving donations</li>
                </ul>
              </div>
            </div>

            {/* Step 3 Details */}
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-number">3</span>
                <h3 className="instruction-title">Get Your URLs</h3>
              </div>
              <div className="instruction-content">
                <p>After creating your page, you'll receive two important URLs:</p>
                <div className="url-examples">
                  <div className="url-box">
                    <h4>OBS URL:</h4>
                    <code>https://streamlab.fun/obs/your-page-id</code>
                    <p>Use this in OBS Studio as a Browser Source</p>
                  </div>
                  <div className="url-box">
                    <h4>Donation URL:</h4>
                    <code>https://streamlab.fun/donate/your-page-id</code>
                    <p>Share this with your viewers for donations</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 Details */}
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-number">4</span>
                <h3 className="instruction-title">Setup OBS Studio</h3>
              </div>
              <div className="instruction-content">
                <ol className="instruction-list">
                  <li>1. Open OBS Studio</li>
                  <li>2. Add a new "Browser Source"</li>
                  <li>3. Paste your OBS URL in the URL field</li>
                  <li>4. Set width: 400px, height: 300px</li>
                  <li>5. Enable "Shutdown source when not visible"</li>
                  <li>6. Add "Chroma Key" filter to remove background</li>
                  <li>7. Position the widget where you want it on your stream</li>
                </ol>
              </div>
            </div>

            {/* Step 5 Details */}
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="instruction-number">5</span>
                <h3 className="instruction-title">Go Live!</h3>
              </div>
              <div className="instruction-content">
                <p>You're all set! When viewers donate through your donation URL, you'll see real-time notifications on your stream.</p>
                <div className="instruction-tips">
                  <h4>Pro Tips:</h4>
                  <ul>
                    <li>• Test your setup before going live</li>
                    <li>• Position the widget where it won't block important content</li>
                    <li>• Share your donation URL in your stream description</li>
                    <li>• Consider setting a reasonable minimum donation amount</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="cta-card">
            <h2 className="cta-title">
              Ready to Start Earning?
            </h2>
            <p className="cta-subtitle">
              Create your donation page now and start receiving SOL donations on your stream
            </p>
            <div className="text-center">
              <button
                onClick={handleGetStarted}
                className="btn-primary btn-lg"
              >
                Create Donation Page
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
